import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, Lock, RotateCcw, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const ORDER = ["1", "1.5", "2", "3", "4", "5"];

const META = {
  "1":   { title: "P1",   note: "1 Mitarbeiter · saubere Ausführung · Standard = P2 × 1,75 · Overrides möglich", editable: false, overridable: true },
  "1.5": { title: "P1.5", note: "1 Mitarbeiter · kleine Qualitätsmängel/Nachbesserung · Standard = P2 × 1,30 · Overrides möglich", editable: false, overridable: true },
  "2":   { title: "P2",   note: "Basispreisliste · 2 Mitarbeiter — jeder erhält den vollen P2-Preis · Editierbar", editable: true,  overridable: false },
  "3":   { title: "P3",   note: "3 Mitarbeiter · P2 × 0,70 · Automatisch abgeleitet", editable: false, overridable: false },
  "4":   { title: "P4",   note: "4 Mitarbeiter · P2 × 0,50 · Automatisch abgeleitet", editable: false, overridable: false },
  "5":   { title: "P5",   note: "Subunternehmer · Editierbar", editable: true,  overridable: false },
};

const fmt = (n) => (Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const emptyPos = { pos_nummer: "", name: "", price: 0 };

export default function PriceLists() {
  const { isAdmin } = useAuth();
  const [detailed, setDetailed] = useState({});
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("2");

  // Base pricelist editor (P2/P5)
  const [openBase, setOpenBase] = useState(false);
  const [baseForm, setBaseForm] = useState(emptyPos);
  const [baseEditingCode, setBaseEditingCode] = useState(null);

  // Override editor (P1/P1.5)
  const [openOverride, setOpenOverride] = useState(false);
  const [overrideRow, setOverrideRow] = useState(null);
  const [overrideValue, setOverrideValue] = useState("");
  const [overrideComment, setOverrideComment] = useState("");

  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pricelists/detailed");
    setDetailed(data);
  };
  useEffect(() => { load(); }, []);

  const startNewBase = () => { setBaseEditingCode(null); setBaseForm(emptyPos); setOpenBase(true); };
  const startEditBase = (row) => {
    setBaseEditingCode(row.pos_nummer);
    setBaseForm({ pos_nummer: row.pos_nummer, name: row.name, price: row.effective_price });
    setOpenBase(true);
  };
  const saveBase = async () => {
    if (!baseForm.pos_nummer || !baseForm.name) { toast.error("Pos.-Nr. und Beschreibung sind Pflicht"); return; }
    setSaving(true);
    try {
      const payload = { pos_nummer: String(baseForm.pos_nummer).trim(), name: baseForm.name.trim(), price: Number(baseForm.price) || 0 };
      if (baseEditingCode) {
        await api.put(`/pricelists/${tab}/positions/${encodeURIComponent(baseEditingCode)}`, payload);
      } else {
        await api.post(`/pricelists/${tab}/positions`, payload);
      }
      toast.success("Position gespeichert");
      setOpenBase(false);
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delBase = async (code) => {
    if (!window.confirm(`Position ${code} aus P${tab} löschen?`)) return;
    try {
      await api.delete(`/pricelists/${tab}/positions/${encodeURIComponent(code)}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const startOverride = (row) => {
    setOverrideRow(row);
    setOverrideValue(row.manual_price != null ? String(row.manual_price) : String(row.auto_price));
    setOverrideComment(row.comment || "");
    setOpenOverride(true);
  };
  const saveOverride = async () => {
    if (!overrideRow) return;
    const val = Number(overrideValue);
    if (!isFinite(val) || val <= 0) { toast.error("Bitte gültigen Preis eingeben"); return; }
    setSaving(true);
    try {
      await api.put(
        `/pricelists/${tab}/positions/${encodeURIComponent(overrideRow.pos_nummer)}/override`,
        { manual_price: val, comment: overrideComment || null },
      );
      toast.success(`P${tab} · ${overrideRow.pos_nummer} manuell gesetzt`);
      setOpenOverride(false);
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const resetOverride = async () => {
    if (!overrideRow) return;
    if (!window.confirm(`Manuellen Preis für ${overrideRow.pos_nummer} in P${tab} zurücksetzen?\nAnschließend wird wieder P2 × ${tab === "1" ? "1,75" : "1,30"} verwendet.`)) return;
    setSaving(true);
    try {
      await api.delete(`/pricelists/${tab}/positions/${encodeURIComponent(overrideRow.pos_nummer)}/override`);
      toast.success("Zurückgesetzt auf automatische Berechnung");
      setOpenOverride(false);
      await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const meta = META[tab];
  const rows = (detailed[tab] || [])
    .filter((row) => !q || (row.pos_nummer + " " + (row.name || "")).toLowerCase().includes(q.toLowerCase()));

  const overridable = tab === "1" || tab === "1.5";
  const factorLabel = tab === "1" ? "P2 × 1,75" : tab === "1.5" ? "P2 × 1,30" : tab === "3" ? "P2 × 0,70" : tab === "4" ? "P2 × 0,50" : null;

  return (
    <div data-testid="pricelists-page">
      <PageHeader
        title="Preislisten"
        subtitle="P1, P1.5, P2, P3, P4, P5 · P1/P1.5 unterstützen manuelle Overrides · Historische Aufträge bleiben unberührt"
        actions={isAdmin && meta?.editable && (
          <Dialog open={openBase} onOpenChange={setOpenBase}>
            <DialogTrigger asChild>
              <Button className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={startNewBase} data-testid="pl-new-btn">
                <Plus className="h-4 w-4 mr-2" /> Neue Position (P{tab})
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{baseEditingCode ? `Position ${baseEditingCode} bearbeiten` : `Neue Position in P${tab}`}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label>Pos.-Nr. *</Label>
                  <Input className="h-11 mt-1 mono" value={baseForm.pos_nummer} onChange={(e) => setBaseForm({ ...baseForm, pos_nummer: e.target.value })} data-testid="pl-pos" placeholder="z. B. 10060209" />
                </div>
                <div>
                  <Label>Beschreibung *</Label>
                  <Input className="h-11 mt-1" value={baseForm.name} onChange={(e) => setBaseForm({ ...baseForm, name: e.target.value })} data-testid="pl-name" placeholder="z. B. GWV Basismodul" />
                </div>
                <div>
                  <Label>Einzelpreis (€) *</Label>
                  <Input className="h-11 mt-1 text-right" type="number" step="0.01" min="0" value={baseForm.price} onChange={(e) => setBaseForm({ ...baseForm, price: e.target.value })} data-testid="pl-price" />
                </div>
                <div className="text-xs text-slate-500 bg-slate-50 border rounded p-3" style={{ borderColor: "#E2E8F0" }}>
                  Änderungen in P2 wirken sich automatisch auf P1 (× 1,75), P1.5 (× 1,30), P3 (× 0,70) und P4 (× 0,50) aus.
                  <br />Manuell gesetzte P1/P1.5-Preise werden dabei nicht überschrieben.
                </div>
              </div>
              <DialogFooter>
                <Button className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={saveBase} disabled={saving} data-testid="pl-save">
                  {saving ? "Speichern…" : "Speichern"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="relative max-w-md mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="h-11 pl-10" placeholder="Pos.-Nr. oder Beschreibung suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pl-search" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {ORDER.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`pl-tab-${k}`}>P{k}</TabsTrigger>
          ))}
        </TabsList>
        {ORDER.map((k) => (
          <TabsContent key={k} value={k}>
            <div className="bg-white border rounded-md p-4 mt-3" style={{ borderColor: "#E2E8F0" }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-lg">{META[k].title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {META[k].note}
                    {!META[k].editable && !META[k].overridable && <Lock className="h-3 w-3" />}
                  </div>
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{(detailed[k] || []).length} Positionen</div>
              </div>
            </div>

            {(k === "1" || k === "1.5") && (
              <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-md p-3 mt-3 text-xs flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Bedeutung:</strong> {k === "1" ? "Alleinausführung mit sauberer, vollständiger Arbeit — höchste Vergütung."
                    : "Alleinausführung, aber Qualitätsmangel/Nachbesserung durch den Mitarbeiter — reduzierte Vergütung."}
                  <br />Standardformel: <strong>{factorLabel}</strong>. Über „Bearbeiten" können einzelne Positionen manuell überschrieben werden.
                </div>
              </div>
            )}

            <div className="bg-white border rounded-md overflow-hidden mt-4" style={{ borderColor: "#E2E8F0" }}>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-3 w-40">Pos.-Nr.</th>
                    <th className="text-left px-4 py-3">Beschreibung</th>
                    {overridable && <th className="text-right px-4 py-3 w-28">P2-Basis</th>}
                    {overridable && <th className="text-right px-4 py-3 w-28">Standard</th>}
                    <th className="text-right px-4 py-3 w-32">{overridable ? "Aktueller Preis" : "Preis (€)"}</th>
                    {overridable && <th className="text-left px-4 py-3 w-32">Status</th>}
                    {(META[k].editable || overridable) && isAdmin && <th className="w-24"></th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-400">Keine Treffer</td></tr>}
                  {rows.map((row) => (
                    <tr key={row.pos_nummer} className="border-t" style={{ borderColor: "#E2E8F0" }} data-testid={`pl-row-${row.pos_nummer}`}>
                      <td className="px-4 py-2.5 mono font-semibold">{row.pos_nummer}</td>
                      <td className="px-4 py-2.5">{row.name}</td>
                      {overridable && <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{fmt(row.p2_price)}</td>}
                      {overridable && <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{fmt(row.auto_price)}</td>}
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmt(row.effective_price)}</td>
                      {overridable && (
                        <td className="px-4 py-2.5">
                          {row.status === "manual" ? (
                            <Badge className="bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-100">
                              Manuell{row.factor_percent != null ? ` · ${(row.factor_percent - 100).toFixed(1)} %` : ""}
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">Automatisch</Badge>
                          )}
                        </td>
                      )}
                      {isAdmin && (META[k].editable || overridable) && (
                        <td className="px-4 py-2.5 flex justify-end gap-2">
                          {META[k].editable && (
                            <>
                              <button className="p-1.5 hover:text-[#0A2540]" onClick={() => startEditBase(row)} data-testid={`pl-edit-${row.pos_nummer}`} title="Bearbeiten">
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button className="p-1.5 hover:text-red-600" onClick={() => delBase(row.pos_nummer)} data-testid={`pl-del-${row.pos_nummer}`} title="Löschen">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          {overridable && (
                            <button className="p-1.5 hover:text-[#0A2540]" onClick={() => startOverride(row)} data-testid={`pl-override-${row.pos_nummer}`} title="Preis anpassen">
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Override-Dialog für P1/P1.5 */}
      <Dialog open={openOverride} onOpenChange={setOpenOverride}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Position {overrideRow?.pos_nummer} bearbeiten</DialogTitle>
            <DialogDescription>P{tab} · {overrideRow?.name}</DialogDescription>
          </DialogHeader>
          {overrideRow && (
            <div className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-slate-50 border rounded p-3" style={{ borderColor: "#E2E8F0" }}>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">P2-Basis</div>
                  <div className="font-bold tabular-nums mt-1">€ {fmt(overrideRow.p2_price)}</div>
                </div>
                <div className="bg-slate-50 border rounded p-3" style={{ borderColor: "#E2E8F0" }}>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Standard-Berechnung</div>
                  <div className="font-bold tabular-nums mt-1">€ {fmt(overrideRow.auto_price)}</div>
                  <div className="text-xs text-slate-400">{factorLabel}</div>
                </div>
                <div className="bg-slate-50 border rounded p-3" style={{ borderColor: "#E2E8F0" }}>
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Aktuell verwendet</div>
                  <div className="font-bold tabular-nums mt-1">€ {fmt(overrideRow.effective_price)}</div>
                  <div className="text-xs text-slate-400">Status: {overrideRow.status === "manual" ? "Manuell" : "Automatisch"}</div>
                </div>
              </div>
              <div>
                <Label>Manueller Preis (€)</Label>
                <Input className="h-11 mt-1 text-right" type="number" step="0.01" min="0" value={overrideValue} onChange={(e) => setOverrideValue(e.target.value)} data-testid="pl-override-value" />
              </div>
              <div>
                <Label>Bemerkung / Änderungsgrund (optional)</Label>
                <Textarea className="mt-1 min-h-[60px]" value={overrideComment} onChange={(e) => setOverrideComment(e.target.value)} placeholder="z. B. Sondervereinbarung, Marktanpassung…" data-testid="pl-override-comment" />
              </div>
              {overrideRow.status === "manual" && (
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-3">
                  Diese Position wurde am {new Date(overrideRow.changed_at).toLocaleString("de-DE")} von <strong>{overrideRow.changed_by}</strong> manuell auf € {fmt(overrideRow.manual_price)} gesetzt.
                  {overrideRow.factor_percent != null && (
                    <>{" "}Aktueller Aufschlag: <strong>+ {(overrideRow.factor_percent - 100).toFixed(1)} %</strong> gegenüber P2.</>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {overrideRow?.status === "manual" && (
              <Button variant="outline" className="h-11" onClick={resetOverride} disabled={saving} data-testid="pl-override-reset">
                <RotateCcw className="h-4 w-4 mr-2" /> Auf automatische Berechnung zurücksetzen
              </Button>
            )}
            <div className="flex-1" />
            <Button className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={saveOverride} disabled={saving} data-testid="pl-override-save">
              {saving ? "Speichern…" : "Manuellen Preis speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ActionBar onPrint={() => window.print()} hideNext />
    </div>
  );
}
